import styled from "styled-components/macro"
import { RowPermission } from "./RowPermission"
import { useIntl } from "react-intl"
import * as Ct from "ldlj"
import { boxShadow, colors } from "../styles/design.config"
import { useEffect } from "react"
import { useRNBSelector } from "../store/rootReducer"
import { useDispatch } from "react-redux"
import { selectUserToEditAction } from "../store/ducks/team.ducks"

interface AuthorisationListProps {
  isSelected: boolean
  content: string
  onClick: () => void
  error: boolean
  closeModal: () => void
  invitedUserMail: string
  id: string
}

export const AuthorisationList = ({
  isSelected = false,
  content,
  onClick,
  error = false,
  closeModal,
  invitedUserMail,
  id,
}: AuthorisationListProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const invitationStatus = useRNBSelector(
    (state) => state.user.userInvitationStatus
  )
  const team = useRNBSelector((state) => state.team.team)
  const editedUserId = useRNBSelector((state) => state.team.editedUserId)

  useEffect(() => {
    if (
      invitationStatus === "SUCCESS" ||
      invitationStatus === "REACTIVATED_SUCCESS"
    ) {
      closeModal()
    }
  }, [invitationStatus, closeModal])

  useEffect(() => {
    if (invitationStatus === "CUSTOMER_SUCCESS") {
      const lastElementOfTeam = Object.entries(team).find(
        (element) => element[1].email === invitedUserMail
      )
      if (lastElementOfTeam !== undefined) {
        dispatch(selectUserToEditAction(Number(lastElementOfTeam[0])))
      }
    }
  }, [team, invitationStatus, dispatch, invitedUserMail])

  useEffect(() => {
    if (editedUserId !== null && invitationStatus === "CUSTOMER_SUCCESS") {
      closeModal()
    }
  }, [editedUserId, closeModal, invitationStatus])

  return (
    <TabContainer
      id={id}
      isSelected={isSelected}
      onClick={onClick}
      error={error}
    >
      <TabHeader isSelected={isSelected} error={error}>
        {content}
      </TabHeader>
      <Ct.Spacer height={3} />
      <List>
        <RowPermission
          isAuthorized={!(id === "ClientInvitation") && !error}
          text={intl.formatMessage({ id: "authorize-list.create-company" })}
        />
        <Ct.Spacer height={1} />
        <RowPermission
          isAuthorized={
            !(id === "ClientInvitation" || id === "CollaboratorInvitation") &&
            !error
          }
          text={intl.formatMessage({
            id: "authorize-list.deactivate-company",
          })}
        />
        <Ct.Spacer height={1} />

        <RowPermission
          text={intl.formatMessage({
            id: "autorize-list.manage-bill-credit-quote",
          })}
        />
        <Ct.Spacer height={1} />
        <RowPermission
          isAuthorized={!error}
          text={intl.formatMessage({ id: "authorize-list.sell-bill" })}
        />
        <Ct.Spacer height={1} />
        <RowPermission
          isAuthorized={!error}
          text={intl.formatMessage({ id: "autorize-list.issue-quote" })}
        />
        <Ct.Spacer height={1} />
        <RowPermission
          isAuthorized={!error}
          text={intl.formatMessage({ id: "autorize-list.issue-credit" })}
        />
        <Ct.Spacer height={1} />
        <RowPermission
          isAuthorized={!(id === "ClientInvitation") && !error}
          text={intl.formatMessage({
            id: "authorize-list.accounting-entries",
          })}
        />
        <Ct.Spacer height={1} />
        <RowPermission
          isAuthorized={
            !(id === "ClientInvitation" || id === "CollaboratorInvitation") &&
            !error
          }
          text={intl.formatMessage({ id: "authorize-list.parameter" })}
        />
        <Ct.Spacer height={1} />
        <RowPermission
          isAuthorized={!error}
          text={intl.formatMessage({ id: "authorize-list.bank" })}
        />
        <Ct.Spacer height={1} />
        <RowPermission
          isAuthorized={!(id === "ClientInvitation") && !error}
          text={intl.formatMessage({ id: "authorize-list.download-bank" })}
        />
        <Ct.Spacer height={2} />
        <PaddingBar>
          <Ct.Bar width={12.5} />
        </PaddingBar>
        <Ct.Spacer height={2} />
        <RowPermission
          isAuthorized={!error}
          text={intl.formatMessage({ id: "authorize-list.bill-email" })}
        />
        <Ct.Spacer height={1} />
        <RowPermission
          isAuthorized={!(id === "ClientInvitation") && !error}
          text={intl.formatMessage({ id: "authorize-list.email-chaintrust" })}
        />
        <Ct.Spacer height={2} />
        <PaddingBar>
          <Ct.Bar width={12.5} />
        </PaddingBar>
        <Ct.Spacer height={2} />
        <RowPermission
          isAuthorized={
            !(id === "ClientInvitation" || id === "CollaboratorInvitation") &&
            !error
          }
          text={intl.formatMessage({ id: "authorize-list.display-team" })}
        />
        <Ct.Spacer height={1} />
        <RowPermission
          isAuthorized={!(id === "ClientInvitation") && !error}
          text={intl.formatMessage({ id: "authorize-list.display-client" })}
        />
      </List>
      <Ct.Spacer height={3} />
      <ButtonList>
        <Ct.Button
          label={intl.formatMessage(
            { id: "authorize-list.invite-user" },
            { userTitle: content }
          )}
          disabled={!isSelected || error}
          type="submit"
          width={21.75}
        />
      </ButtonList>
      <Ct.Spacer height={3} />
    </TabContainer>
  )
}

interface Cliked {
  isSelected: boolean
  error: boolean
}

export const TabContainer = styled.div<Cliked>`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  align-items: left;
  background-color: ${colors.white};
  border-radius: 1.5rem;
  border: ${({ isSelected, error }) =>
    isSelected && !error
      ? `0.25rem solid ${colors.cornflower}`
      : error
      ? `${colors.desertStorm}`
      : `0.25rem solid ${colors.hawkes}`};
  border-width: 0 0.25rem 0.25rem 0.25rem;
  box-sizing: border-box;
  box-shadow: ${boxShadow};
  pointer-events: ${({ error }) => (error ? `none` : `auto`)};
`

export const TabHeader = styled.div<Cliked>`
  position: relative;
  width: 100%;
  height: 6.5rem;
  border-radius: 1.25rem 1.25rem 0rem 0rem;
  background-color: ${({ isSelected, error }) =>
    isSelected && !error
      ? `${colors.cornflower}`
      : error
      ? `${colors.desertStorm}`
      : `${colors.hawkes}`};
  color: ${({ isSelected, error }) =>
    isSelected && !error
      ? `${colors.white}`
      : error
      ? `${colors.moon}`
      : `${colors.navy}`};
  justify-content: center;
  flex-direction: column;
  display: flex;
  text-transform: uppercase;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 2.25rem;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0 4rem 0 4rem;
`

const ButtonList = styled.div`
  display: flex;
  align-self: center;
`

const PaddingBar = styled.div`
  padding: 0 0 0 4rem;
`
